/**
 * The spoken-guidance controls, in the instruction card (SPEC_CAPTURE_UX_R2 §3.1).
 *
 *   · REPLAY — always visible, `aria-label` "Play the instructions again." It unlocks the player if the customer
 *     has not tapped anything else yet, so it is never a dead control (P10);
 *   · MUTE — a `button[aria-pressed]` (the grid-toggle precedent), persisted per link, `aria-label` "Turn guidance
 *     sound off" / "Turn guidance sound on";
 *   · SHOW ME — the §4 demonstration for this shot, one tap from the card as well as from the help sheet;
 *   · the rung-3 NOTE — when neither a server asset nor a device voice could speak this language, the honest line
 *     ("Audio guidance is not available in <language> yet — read the steps below.") instead of silence with no
 *     explanation. The note appears only after an attempt actually reached rung 3 — never as a pre-emptive apology.
 */

import { ownsGuidanceNote, useGuidance } from '@/capture/guidance/guidanceStore';
import { NATIVE_NAMES, useT } from '@/i18n/runtime';
import { track } from '@/flow/instrument';
import type { DemoType } from '../demos/demoTypes';
import { useDemoSheet } from '../demos/demoStore';
import { Icon } from './Icon';

/**
 * Rung 3, visible (§3.4 #3): no reviewed asset and no device voice for this language ⇒ the honest line, never
 * silence with no explanation and never a claim that audio is coming. Rendered where the controls are, and on the
 * consent screen under "Listen to this notice".
 *
 * `once` (the instruction card, FSD-20 Addendum C): said on the first step that reaches rung 3 in this language, not
 * repeated on every later card over the camera — the replay button stays, and pressing it is the honest test.
 */
export function GuidanceNote({ testId = 'audio-unavailable', once = false }: { testId?: string; once?: boolean }): JSX.Element | null {
  const { t, locale } = useT();
  const guidance = useGuidance();
  if (guidance.state.muted || guidance.state.source !== 'NONE') return null;
  if (once && !ownsGuidanceNote(locale, guidance.state.cueKey ?? '')) return null;
  return (
    <p className="sv-guide-note" role="note" data-testid={testId}>
      {t('sv.audio.unavailable', { language: NATIVE_NAMES[locale] ?? locale })}
    </p>
  );
}

export function GuidanceControls({ demoType }: { demoType?: DemoType | null }): JSX.Element {
  const { t, locale } = useT();
  const guidance = useGuidance();
  const sheet = useDemoSheet();
  const muted = guidance.state.muted;
  return (
    <div className="sv-guide-row" data-testid="guidance-controls" data-source={guidance.state.source ?? 'idle'}>
      <div className="sv-guide-btns">
        <button
          type="button"
          className="sv-guide-btn"
          aria-label={t('sv.audio.replay')}
          onClick={() => {
            track('audio.replay', { locale });
            guidance.replay();
          }}
          data-testid="audio-replay"
        >
          <Icon name="speaker" size={16} />
          <Icon name="redo" size={12} className="sv-guide-sub" />
        </button>
        <button
          type="button"
          className="sv-guide-btn"
          aria-pressed={muted}
          aria-label={muted ? t('sv.audio.unmute') : t('sv.audio.mute')}
          onClick={() => {
            track('audio.mute', { locale, muted: !muted });
            guidance.toggleMuted();
          }}
          data-testid="audio-mute"
        >
          <Icon name={muted ? 'speaker-off' : 'speaker'} size={16} />
        </button>
        {demoType ? (
          <button
            type="button"
            className="sv-guide-btn is-wide"
            onClick={() => {
              // §10 "Show-me replay → replay rate per shot type": a high rate means the demonstration is not landing.
              track('showme.replay', { type: demoType, where: 'card' });
              sheet.show(demoType);
            }}
            data-testid="show-me-card"
          >
            <Icon name="play" size={14} /> {t('sv.help.showMe')}
          </button>
        ) : null}
      </div>
      <GuidanceNote once />
    </div>
  );
}
